import { useEffect, useMemo, useState, useCallback } from 'react';
import api from '../services/api';
import { Building2, Dumbbell, Users, Search, Plus, Save, Layers, CalendarPlus, Trash2 } from 'lucide-react';
import { useAuth } from '../context/useAuth';
import { useI18n } from '../context/useI18n';
import { emitToast } from '../context/toast';
import InviteCodeCell from './admin/InviteCodeCell';
import { filterUsersByScope } from '../utils/adminUserScopes';
import ExtendVigenciaModal from './admin/ExtendVigenciaModal';
import ManageOrgLicensesModal from './admin/ManageOrgLicensesModal';
import OrganizationsD28dTree from './admin/OrganizationsD28dTree';
import CreateHoldingModal from './admin/CreateHoldingModal';
import OrganizationsCoachList from './admin/OrganizationsCoachList';
import CreateCoachOrgModal from './admin/CreateCoachOrgModal';
import { isFitLiveUiEnabled } from '../utils/fitLiveModule';
import { FitLiveTrainersRoster } from '../fit-live/FitLiveAdminSurfaces';
import {
  findOrgOperator,
  formatDateLabel,
  formatDaysRemaining,
  summarizeOperatorSubscription,
  summarizeSubscription,
} from '../utils/organizationLicenseSummary';
import {
  collectOrgLicenses,
  isGymActive,
  isOrgRowVisible,
  isTrainerActive,
  orgDisplayName,
  orgTypeLabel,
  readOrgInitialTab,
} from '../utils/organizationView';

/** Acepta array directo o respuestas envueltas ({ data }, { gyms }, etc.). */
function asArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') {
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.gyms)) return payload.gyms;
    if (Array.isArray(payload.trainers)) return payload.trainers;
    if (Array.isArray(payload.planes)) return payload.planes;
    if (Array.isArray(payload.plans)) return payload.plans;
  }
  return [];
}

function LicenseChips({ operator }) {
  const licenses = collectOrgLicenses(operator);
  if (!licenses.length) return <span className="text-xs text-stone-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {licenses.map((lic) => (
        <span
          key={`${lic.label}-${lic.code}`}
          className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-sky-100 text-sky-900"
        >
          {lic.label}
        </span>
      ))}
    </div>
  );
}

function AdminCompaniesRegistry({ onExtendOperator, onManageLicenses, reloadNonce = 0 }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [companyTenants, setCompanyTenants] = useState([]);
  const [companySearch, setCompanySearch] = useState('');
  const [companyLoading, setCompanyLoading] = useState(true);
  const [gyms, setGyms] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [extendTarget, setExtendTarget] = useState(null);
  const [extendSaving, setExtendSaving] = useState(false);
  const useParentExtend = Boolean(onExtendOperator);

  const load = useCallback(async () => {
    setCompanyLoading(true);
    try {
      const [tenRes, gymRes, trainerRes, userRes, planRes] = await Promise.all([
        api.get('/platform/tenants'),
        api.get('/gyms'),
        api.get('/trainers'),
        api.get('/admin/users'),
        api.get('/accounts/plans', { params: { kind: 'd28d', platform_only: 'true' } }),
      ]);
      const rows = (tenRes.data?.data || []).filter((row) => {
        const type = String(row.tenant_type || '').toLowerCase();
        return ['company', 'organization', 'gym', 'trainer'].includes(type);
      });
      rows.sort((a, b) => {
        const anchorRank = (r) => {
          const code = String(r.tenant_code || '').toLowerCase();
          const name = String(r.tenant_name || '').toLowerCase();
          if (code.includes('d28d') || name.includes('d28d')) return 0;
          if (code.includes('nicolas') || name.includes('nicolas')) return 1;
          const ty = String(r.tenant_type || '').toLowerCase();
          if (ty === 'gym') return 2;
          if (ty === 'trainer') return 3;
          return 4;
        };
        const d = anchorRank(a) - anchorRank(b);
        if (d !== 0) return d;
        return Number(a.tenant_id ?? a.id ?? 0) - Number(b.tenant_id ?? b.id ?? 0);
      });
      setCompanyTenants(rows);
      setGyms(asArray(gymRes.data));
      setTrainers(asArray(trainerRes.data));
      setUsers(userRes.data?.data || []);
      setPlans(asArray(planRes.data));
    } catch {
      setCompanyTenants([]);
    } finally {
      setCompanyLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, reloadNonce]);

  const gymsById = useMemo(
    () => Object.fromEntries(asArray(gyms).map((g) => [g.id, g])),
    [gyms],
  );

  const trainersById = useMemo(
    () => Object.fromEntries(asArray(trainers).map((tr) => [tr.id, tr])),
    [trainers],
  );

  const resolveRow = useCallback((row) => {
    const type = String(row.tenant_type || '').toLowerCase();
    const gym = row.gym_id != null ? gymsById[row.gym_id] : null;
    const trainer = row.trainer_id != null ? trainersById[row.trainer_id] : null;
    const operator = findOrgOperator(users, { gymId: row.gym_id, trainerId: row.trainer_id });
    const sub = summarizeOperatorSubscription(operator);
    const moduleCode = type === 'trainer' ? 'training' : 'd28d';
    const isSede = Boolean(gym?.parent_gym_id);
    return {
      operator,
      entity: gym || trainer,
      displayName: orgDisplayName(gym || trainer, row),
      typeLabel: orgTypeLabel({ tenantType: type, gym, isSede }),
      validFrom: sub.validFrom,
      validUntil: sub.validUntil,
      daysLeft: sub.daysLeft,
      licenseState: sub.estado,
      licenseActive: sub.active,
      moduleCode,
      licenses: collectOrgLicenses(operator),
      sedeGym: isSede ? gym : null,
    };
  }, [gymsById, trainersById, users]);

  const handleDeleteSede = async (gym, orgName) => {
    if (!gym?.id) return;
    const confirmMsg = t(
      'orgs.delete_sede_confirm',
      '¿Eliminar la sede «{name}»? Se archiva la sede y se da de baja a su administrador y a todos sus usuarios.',
    ).replace('{name}', orgName || `#${gym.id}`);
    if (!window.confirm(confirmMsg)) return;
    try {
      const { data } = await api.delete(`/gyms/${gym.id}/sede`);
      emitToast({
        type: 'success',
        title: t('orgs.sede_deleted', 'Sede eliminada'),
        message: t('orgs.sede_deleted_users', '{count} usuarios dados de baja').replace('{count}', data?.users_deleted ?? 0),
      });
      load();
    } catch (e) {
      emitToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: e.response?.data?.error || t('orgs.sede_delete_error', 'No se pudo eliminar la sede'),
      });
    }
  };

  const confirmExtend = async (days) => {
    if (!extendTarget?.userId) return;
    setExtendSaving(true);
    try {
      await api.post(`/payment-admin/extend/${extendTarget.userId}`, {
        module_code: extendTarget.moduleCode || 'd28d',
        days,
      });
      emitToast({
        type: 'success',
        title: t('vigencias.extended', 'Vigencia extendida'),
        message: `+${days} días`,
      });
      setExtendTarget(null);
      load();
    } catch (e) {
      emitToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: e.response?.data?.error || t('users.sub_extend_error', 'No se pudo extender'),
      });
    } finally {
      setExtendSaving(false);
    }
  };

  const filteredCompanyTenants = useMemo(() => {
    const visible = companyTenants.filter((row) => isOrgRowVisible(row, gymsById, trainersById));
    const q = companySearch.trim().toLowerCase();
    if (!q) return visible;
    return visible.filter((row) => {
      const info = resolveRow(row);
      const haystack = [
        row.tenant_code,
        row.tenant_name,
        info.displayName,
        info.typeLabel,
        info.operator?.email,
        ...info.licenses.map((l) => l.label),
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [companyTenants, companySearch, gymsById, trainersById, resolveRow]);

  const roles = Array.isArray(user?.roles) && user.roles.length ? user.roles : [user?.rol].filter(Boolean);
  if (!roles.includes('super_admin')) {
    return (
      <div className="card">
        <p className="text-stone-700">Solo super_admin puede ver Empresas.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-2xl font-bold text-stone-900">{t('orgs.list_all', 'Todas las organizaciones')}</h2>
        <p className="text-stone-600">{t('orgs.list_all_desc', 'Nombre, tipo, administrador y licencias activas. Una organización puede tener varias licencias simultáneas.')}</p>
      </header>
      <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden ui-light-panel">
        <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-stone-600">
            {companyLoading
              ? t('common.loading', 'Cargando…')
              : t('companies.count', '{count} empresas').replace('{count}', filteredCompanyTenants.length)}
          </p>
          <div className="relative w-full sm:max-w-xs">
            <Search className="w-4 h-4 text-stone-500 absolute left-2 top-2.5" />
            <input className="input pl-7 w-full" placeholder={t('common.search', 'Buscar')} value={companySearch} onChange={(e) => setCompanySearch(e.target.value)} />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-stone-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">{t('orgs.organization', 'Organización')}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">{t('orgs.type_label', 'Tipo')}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">{t('orgs.licenses', 'Licencias')}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">{t('common.status', 'Estado')}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">{t('vigencias.valid_until', 'Vigencia admin')}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">{t('common.actions', 'Acciones')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {companyLoading ? (
                <tr><td colSpan="7" className="px-4 py-6 text-center text-stone-500">{t('common.loading', 'Cargando…')}</td></tr>
              ) : filteredCompanyTenants.length === 0 ? (
                <tr><td colSpan="7" className="px-4 py-6 text-center text-stone-500">{t('companies.none', 'Sin empresas registradas.')}</td></tr>
              ) : filteredCompanyTenants.map((row) => {
                const info = resolveRow(row);
                const estadoLabel = info.licenseState || row.status || '—';
                return (
                <tr key={row.tenant_id ?? row.id} className="hover:bg-stone-50">
                  <td className="px-4 py-3 font-medium">
                    {info.displayName}
                    {info.operator ? (
                      <div className="text-xs text-stone-500 font-normal mt-0.5">{info.operator.email}</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-xs text-stone-700">{info.typeLabel}</td>
                  <td className="px-4 py-3"><LicenseChips operator={info.operator} /></td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                      info.licenseActive ? 'bg-emerald-100 text-emerald-800' : 'bg-stone-100 text-stone-700'
                    }`}>
                      {estadoLabel}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap">
                    {info.validUntil ? `${formatDateLabel(info.validUntil)} · ${formatDaysRemaining(info.daysLeft)}` : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      {info.operator ? (
                        <button
                          type="button"
                          className="btn-secondary text-xs py-1 px-2"
                          onClick={() => onManageLicenses?.({
                            operator: info.operator,
                            orgName: info.displayName,
                          })}
                        >
                          {t('orgs.licenses', 'Licencias')}
                        </button>
                      ) : null}
                      {info.sedeGym ? (
                        <button
                          type="button"
                          className="admin-action admin-action--danger"
                          onClick={() => handleDeleteSede(info.sedeGym, info.displayName)}
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>{t('common.delete', 'Eliminar')}</span>
                        </button>
                      ) : null}
                      {!info.operator && !info.sedeGym ? (
                        <span className="text-stone-400 text-xs">—</span>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      </div>

      {!useParentExtend ? (
        <ExtendVigenciaModal
          open={Boolean(extendTarget)}
          userName={extendTarget?.userName || ''}
          planName={extendTarget?.planName || ''}
          moduleLabel={extendTarget?.moduleCode === 'training' ? 'Training' : 'D28D'}
          saving={extendSaving}
          onClose={() => !extendSaving && setExtendTarget(null)}
          onConfirm={confirmExtend}
        />
      ) : null}
    </div>
  );
}

function UserAssignmentsPanel({ embedded = false }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [gyms, setGyms] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selectedGym, setSelectedGym] = useState(null);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [searchGym, setSearchGym] = useState('');
  const [searchTrainer, setSearchTrainer] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ nombre: '', email: '', password: '', planId: '' });
  const [d28dCodes, setD28dCodes] = useState([]);
  const [extendTarget, setExtendTarget] = useState(null);
  const [extendSaving, setExtendSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [g, t, u, p] = await Promise.all([
        api.get('/gyms'),
        api.get('/trainers'),
        api.get('/admin/users'),
        api.get('/accounts/plans', { params: { platform_only: 'true', kind: 'd28d', visible: 'true' } }),
      ]);
      setGyms(asArray(g.data));
      setTrainers(asArray(t.data));
      setUsers(u.data?.data || []);
      setPlans(asArray(p.data));
      api.get('/admin/invite-codes')
        .then((meta) => setD28dCodes(meta.data?.d28d_codes || []))
        .catch(() => setD28dCodes([]));
    } catch {
      setError(t('companies.error_loading', 'Error cargando datos'));
    }
  }, [t]);

  const saveGymInviteCode = async (gymId, code) => {
    const { data } = await api.put(`/gyms/${gymId}`, { invite_code: code });
    const updated = data.gym || data;
    setGyms((prev) => prev.map((g) => (g.id === gymId ? { ...g, invite_code: updated.invite_code } : g)));
    if (selectedGym?.id === gymId) setSelectedGym((g) => ({ ...g, invite_code: updated.invite_code }));
    return updated.invite_code;
  };

  const saveTrainerInviteCode = async (trainerId, code) => {
    const { data } = await api.put(`/trainers/${trainerId}`, { invite_code: code });
    const updated = data.trainer || data;
    setTrainers((prev) => prev.map((tr) => (tr.id === trainerId ? { ...tr, invite_code: updated.invite_code } : tr)));
    if (selectedTrainer?.id === trainerId) setSelectedTrainer((tr) => ({ ...tr, invite_code: updated.invite_code }));
    return updated.invite_code;
  };

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    if (!selectedGym?.id) return;
    api.get('/accounts/plans', { params: { gym_id: selectedGym.id, kind: 'd28d', visible: 'true' } })
      .then((res) => setPlans(asArray(res.data)))
      .catch(() => {});
  }, [selectedGym?.id]);

  useEffect(() => {
    if (selectedGym && !isGymActive(gyms.find((g) => g.id === selectedGym.id))) {
      setSelectedGym(null);
    }
    if (selectedTrainer && !isTrainerActive(trainers.find((t) => t.id === selectedTrainer.id))) {
      setSelectedTrainer(null);
    }
  }, [gyms, trainers, selectedGym, selectedTrainer]);

  const filteredGyms = useMemo(() => {
    const q = searchGym.toLowerCase();
    return gyms.filter((g) => isGymActive(g) && (g.nombre.toLowerCase().includes(q) || g.ciudad?.toLowerCase().includes(q)));
  }, [gyms, searchGym]);

  const filteredTrainers = useMemo(() => {
    const q = searchTrainer.toLowerCase();
    return trainers.filter((t) => isTrainerActive(t) && (t.nombre.toLowerCase().includes(q) || t.email?.toLowerCase().includes(q)));
  }, [trainers, searchTrainer]);

  const assignedUsers = useMemo(() => {
    return filterUsersByScope(users, 'd28d').filter((u) => {
      if (selectedGym) return (u.gym_id || u.gymId) === selectedGym.id;
      if (selectedTrainer) return u.trainer_id === selectedTrainer.id;
      return false;
    });
  }, [users, selectedGym, selectedTrainer]);

  const resetMsg = () => {
    setTimeout(() => {
      setMessage('');
      setError('');
    }, 2000);
  };

  const createUser = async (e) => {
    e.preventDefault();
    if (!form.planId) {
      setError('Selecciona un plan de suscripción para el usuario final');
      resetMsg();
      return;
    }
    try {
      const payload = {
        nombre: form.nombre,
        email: form.email,
        password: form.password || undefined,
        rol: 'usuario_final',
        gym_id: selectedGym?.id ?? null,
        trainer_id: selectedTrainer?.id ?? null,
        planId: form.planId || undefined,
      };
      const res = await api.post('/admin/users', payload);
      const newId = res.data?.data?.id;
      if (newId && (payload.planId !== undefined || payload.gym_id !== null || payload.trainer_id !== null)) {
        await api.put(`/admin/users/${newId}/assign`, {
          gym_id: payload.gym_id,
          trainer_id: payload.trainer_id,
          planId: payload.planId,
        });
      }
      setMessage('Usuario creado');
      setForm({ nombre: '', email: '', password: '', planId: '' });
      setCreating(false);
      const updated = await api.get('/admin/users');
      setUsers(updated.data?.data || []);
      resetMsg();
    } catch (e2) {
      setError(e2.response?.data?.error || 'Error creando usuario');
      resetMsg();
    }
  };

  const updateUserPlan = async (userId, planId) => {
    try {
      await api.put(`/admin/users/${userId}/assign`, {
        gym_id: selectedGym?.id ?? null,
        trainer_id: selectedTrainer?.id ?? null,
        planId: planId || null,
      });
      const updated = await api.get('/admin/users');
      setUsers(updated.data?.data || []);
      setMessage('Plan actualizado');
      resetMsg();
    } catch {
      setError('Error actualizando plan');
      resetMsg();
    }
  };

  const confirmExtendUser = async (days) => {
    if (!extendTarget?.id) return;
    setExtendSaving(true);
    try {
      await api.post(`/payment-admin/extend/${extendTarget.id}`, {
        module_code: 'd28d',
        days,
      });
      const updated = await api.get('/admin/users');
      setUsers(updated.data?.data || []);
      setMessage(t('vigencias.extended', 'Vigencia extendida'));
      setExtendTarget(null);
      resetMsg();
    } catch (e) {
      setError(e.response?.data?.error || t('users.sub_extend_error', 'No se pudo extender'));
      resetMsg();
    } finally {
      setExtendSaving(false);
    }
  };

  const roles = Array.isArray(user?.roles) && user.roles.length ? user.roles : [user?.rol].filter(Boolean);
  if (!roles.includes('super_admin')) {
    return (
      <div className="card">
        <p className="text-stone-700">Solo super_admin puede ver Empresas.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!embedded ? (
        <header>
          <h2 className="text-2xl font-bold text-stone-900">{t('assignments.title', 'Usuarios y asignaciones')}</h2>
          <p className="text-stone-600 mt-1">
            {t('assignments.subtitle', 'Herramienta administrativa: crea usuarios finales, asígnalos a un gimnasio o coach, define su plan y gestiona códigos de invitación. No es un tipo de organización.')}
          </p>
        </header>
      ) : null}
      {d28dCodes.length > 0 && (
        <div className="card bg-lime-50 border-lime-200">
          <p className="font-semibold text-stone-900 text-sm">{t('companies.d28d_codes', 'Códigos D28D para registro')}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {d28dCodes.map((code) => (
              <code key={code} className="text-xs font-mono bg-white border border-lime-300 px-2 py-1 rounded">{code}</code>
            ))}
          </div>
        </div>
      )}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-stone-900 flex items-center gap-2">
            <Building2 className="text-lime-500" />
            {t('companies.gyms', 'Gimnasios')}
          </h3>
          <div className="flex items-center gap-2">
            <p className="text-xs text-stone-500 hidden sm:block">Crear/editar en D28D → Gimnasios</p>
            <div className="relative w-40">
              <Search className="w-4 h-4 text-stone-500 absolute left-2 top-2.5" />
              <input className="input pl-7" placeholder={t('common.search', 'Buscar')} value={searchGym} onChange={e => setSearchGym(e.target.value)} />
            </div>
          </div>
        </div>
        <ul className="divide-y divide-slate-200">
          {filteredGyms.map(g => (
            <li key={g.id} className={`py-2 px-2 rounded-lg hover:bg-stone-50 cursor-pointer ${selectedGym?.id === g.id ? 'bg-stone-100' : ''}`} onClick={() => { setSelectedGym(g); setSelectedTrainer(null); }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-stone-600" />
                  <span className="text-sm text-stone-900">{g.nombre}</span>
                </div>
                <span className="text-xs text-stone-600">{g.ciudad || ''}</span>
              </div>
              {g.invite_code && (
                <code className="mt-1 block text-[10px] font-mono text-lime-800 bg-lime-50 px-1.5 py-0.5 rounded border border-lime-200">
                  {g.invite_code}
                </code>
              )}
            </li>
          ))}
          {filteredGyms.length === 0 && <li className="text-sm text-stone-600">{t('companies.no_gyms', 'Sin gimnasios')}</li>}
        </ul>
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-stone-900 flex items-center gap-2">
            <Dumbbell className="text-lime-500" />
            {t('companies.trainers', 'Entrenadores')}
          </h3>
          <div className="relative w-40">
            <Search className="w-4 h-4 text-stone-500 absolute left-2 top-2.5" />
            <input className="input pl-7" placeholder={t('common.search', 'Buscar')} value={searchTrainer} onChange={e => setSearchTrainer(e.target.value)} />
          </div>
        </div>
        <ul className="divide-y divide-slate-200">
          {filteredTrainers.map(t => (
            <li key={t.id} className={`py-2 px-2 rounded-lg hover:bg-stone-50 cursor-pointer ${selectedTrainer?.id === t.id ? 'bg-stone-100' : ''}`} onClick={() => { setSelectedTrainer(t); setSelectedGym(null); }}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Users className="w-4 h-4 text-stone-600 shrink-0" />
                  <span className="text-sm text-stone-900 truncate">{t.nombre}</span>
                </div>
                <span className="text-xs text-stone-600 shrink-0 truncate">{t.email || ''}</span>
              </div>
              {t.invite_code && (
                <code className="mt-1 block text-[10px] font-mono text-lime-800 bg-lime-50 px-1.5 py-0.5 rounded border border-lime-200 truncate">
                  {t.invite_code}
                </code>
              )}
            </li>
          ))}
          {filteredTrainers.length === 0 && <li className="text-sm text-stone-600">{t('companies.no_trainers', 'Sin entrenadores')}</li>}
        </ul>
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-stone-900">
            {selectedGym ? t('companies.users_of_gym', 'Usuarios del Gimnasio: {name}').replace('{name}', selectedGym.nombre) : selectedTrainer ? t('companies.users_of_trainer', 'Usuarios del Entrenador: {name}').replace('{name}', selectedTrainer.nombre) : t('companies.select_prompt', 'Selecciona un gimnasio o entrenador')}
          </h3>
          {(selectedGym || selectedTrainer) && !creating && (
            <button className="btn-primary inline-flex items-center gap-2" onClick={() => setCreating(true)}>
              <Plus size={16} />
              {t('companies.new_user', 'Nuevo usuario')}
            </button>
          )}
        </div>

        {message && <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl p-2 mb-3">{message}</div>}
        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-2 mb-3">{error}</div>}

        {selectedGym && (
          <div className="mb-4 p-3 rounded-xl border border-lime-200 bg-lime-50/50">
            <p className="text-xs font-semibold text-stone-600 uppercase mb-2">{t('users.invite_code', 'Código invitación')} — {selectedGym.nombre}</p>
            <InviteCodeCell shareLabel="Gimnasio — QR registro" value={selectedGym.invite_code} onSave={(code) => saveGymInviteCode(selectedGym.id, code)} />
          </div>
        )}
        {selectedTrainer && (
          <div className="mb-4 p-3 rounded-xl border border-lime-200 bg-lime-50/50">
            <p className="text-xs font-semibold text-stone-600 uppercase mb-2">{t('users.invite_code', 'Código invitación')} — {selectedTrainer.nombre}</p>
            <InviteCodeCell shareLabel="Entrenador — QR registro" value={selectedTrainer.invite_code} onSave={(code) => saveTrainerInviteCode(selectedTrainer.id, code)} />
          </div>
        )}

        {creating && (selectedGym || selectedTrainer) && (
          <form onSubmit={createUser} className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <input className="input" placeholder={t('common.name', 'Nombre')} value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} required />
            <input className="input" placeholder={t('common.email', 'Email')} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
            <input className="input" placeholder={t('companies.password_optional', 'Contraseña (opcional)')} type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
            <select className="input" value={form.planId} onChange={e => setForm({ ...form, planId: e.target.value })} required>
              <option value="">{t('companies.select_plan', 'Seleccionar plan…')}</option>
              {plans.map(p => <option key={p.nombre} value={p.nombre}>{p.nombre}</option>)}
            </select>
            <div className="md:col-span-2 flex gap-2">
              <button type="submit" className="btn-primary inline-flex items-center gap-2">
                <Save size={16} />
                {t('common.create', 'Crear')}
              </button>
              <button type="button" className="btn-secondary" onClick={() => setCreating(false)}>{t('common.cancel', 'Cancelar')}</button>
            </div>
          </form>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-stone-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-stone-600 uppercase">{t('common.user', 'Usuario')}</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-stone-600 uppercase">{t('common.role', 'Rol')}</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-stone-600 uppercase">{t('common.plan', 'Plan')}</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-stone-600 uppercase">{t('vigencias.valid_until', 'Vigencia')}</th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-stone-600 uppercase">{t('common.actions', 'Acciones')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {assignedUsers.map(u => {
                const sub = summarizeSubscription(u.subscription);
                return (
                <tr key={u.id} className="hover:bg-stone-50">
                  <td className="px-4 py-2 text-sm text-stone-900">{u.nombre}<div className="text-xs text-stone-600">{u.email}</div></td>
                  <td className="px-4 py-2 text-sm text-stone-700">{u.rol}</td>
                  <td className="px-4 py-2 text-sm">
                    <select className="input text-sm py-1" defaultValue={u.planId || ''} onChange={e => updateUserPlan(u.id, e.target.value)}>
                      <option value="">{t('companies.no_plan', 'Sin plan')}</option>
                      {plans.map(p => <option key={p.nombre} value={p.nombre}>{p.nombre}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-2 text-sm text-stone-700">
                    {sub.validUntil ? (
                      <>
                        {formatDateLabel(sub.validUntil)}
                        <span className="text-xs text-stone-500 block">{formatDaysRemaining(sub.daysLeft)}</span>
                      </>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-2 text-sm text-right">
                    {u.subscription?.has_account && u.subscription?.active ? (
                      <button
                        type="button"
                        className="btn-secondary text-xs inline-flex items-center gap-1"
                        onClick={() => setExtendTarget(u)}
                      >
                        <CalendarPlus className="w-3 h-3" />
                        {t('vigencias.extend_short', 'Ampliar')}
                      </button>
                    ) : null}
                  </td>
                </tr>
              );})}
              {assignedUsers.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-sm text-stone-600 text-center">{t('companies.no_assigned', 'No hay usuarios asociados')}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <ExtendVigenciaModal
      open={Boolean(extendTarget)}
      userName={extendTarget?.nombre || ''}
      planName={extendTarget?.subscription?.plan || extendTarget?.planId || ''}
      moduleLabel="D28D"
      saving={extendSaving}
      onClose={() => !extendSaving && setExtendTarget(null)}
      onConfirm={confirmExtendUser}
    />
    </div>
  );
}

export { UserAssignmentsPanel };

export default function AdminCompanies({
  mode = 'organizations',
  initialTab,
  onNavigate,
  onOpenGymPanel,
}) {
  const { t } = useI18n();
  const { user } = useAuth();
  const roles = useMemo(
    () => (Array.isArray(user?.roles) && user.roles.length ? user.roles : [user?.rol].filter(Boolean)),
    [user],
  );
  const canCreateHolding = roles.some((r) => ['super_admin', 'admin_d28d'].includes(r));
  const [showCreateHolding, setShowCreateHolding] = useState(false);
  const [showCreateCoach, setShowCreateCoach] = useState(false);
  if (mode === 'convenios' || mode === 'asignaciones') return <UserAssignmentsPanel />;

  const [orgTab, setOrgTab] = useState(() => initialTab || readOrgInitialTab());
  const [gyms, setGyms] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [users, setUsers] = useState([]);
  const [sharedLoading, setSharedLoading] = useState(true);
  const [extendTarget, setExtendTarget] = useState(null);
  const [extendSaving, setExtendSaving] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [licenseTarget, setLicenseTarget] = useState(null);

  const loadShared = useCallback(async () => {
    setSharedLoading(true);
    try {
      const [gymRes, trainerRes, userRes] = await Promise.all([
        api.get('/gyms'),
        api.get('/trainers'),
        api.get('/admin/users'),
      ]);
      setGyms(asArray(gymRes.data));
      setTrainers(asArray(trainerRes.data));
      setUsers(userRes.data?.data || []);
    } catch {
      setGyms([]);
      setTrainers([]);
      setUsers([]);
    } finally {
      setSharedLoading(false);
    }
  }, []);

  useEffect(() => {
    loadShared();
  }, [loadShared]);

  const confirmExtend = async (days) => {
    if (!extendTarget?.userId) return;
    setExtendSaving(true);
    try {
      await api.post(`/payment-admin/extend/${extendTarget.userId}`, {
        module_code: extendTarget.moduleCode || 'd28d',
        days,
      });
      emitToast({
        type: 'success',
        title: t('vigencias.extended', 'Vigencia extendida'),
        message: `+${days} días`,
      });
      setExtendTarget(null);
      setReloadNonce((n) => n + 1);
      loadShared();
    } catch (e) {
      emitToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: e.response?.data?.error || t('users.sub_extend_error', 'No se pudo extender'),
      });
    } finally {
      setExtendSaving(false);
    }
  };

  const extendEndUser = async ({ userId, moduleCode, days }) => {
    setExtendSaving(true);
    try {
      await api.post(`/payment-admin/extend/${userId}`, {
        module_code: moduleCode || 'd28d',
        days,
      });
      emitToast({
        type: 'success',
        title: t('vigencias.extended', 'Vigencia extendida'),
        message: `+${days} días`,
      });
      setReloadNonce((n) => n + 1);
      await loadShared();
    } catch (e) {
      emitToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: e.response?.data?.error || t('users.sub_extend_error', 'No se pudo extender'),
      });
      throw e;
    } finally {
      setExtendSaving(false);
    }
  };

  const saveTrainerInviteCode = async (trainerId, code) => {
    const { data } = await api.put(`/trainers/${trainerId}`, { invite_code: code });
    const updated = data.trainer || data;
    setTrainers((prev) => prev.map((tr) => (tr.id === trainerId ? { ...tr, invite_code: updated.invite_code } : tr)));
    return updated.invite_code;
  };

  const tabs = [
    { id: 'todas', label: t('orgs.tab.all', 'Todas'), Icon: Layers },
    {
      id: 'gimnasios',
      label: isFitLiveUiEnabled()
        ? t('orgs.tab.fitlive', 'Entrenadores Fit Live')
        : t('orgs.tab.gyms', 'Gimnasios D28D'),
      Icon: Building2,
    },
    { id: 'coaches', label: t('orgs.tab.coaches', 'Coaches'), Icon: Dumbbell },
  ];

  return (
    <div className="dashboard-main-view space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-stone-900">{t('orgs.title', 'Organizaciones')}</h2>
          <p className="text-stone-600">
            {isFitLiveUiEnabled()
              ? t('orgs.subtitle_fitlive', 'Organizaciones del ecosistema, entrenadores Fit Live y coaches Training. Una organización puede tener varias licencias activas.')
              : t('orgs.subtitle', 'Organizaciones del ecosistema, sedes y licencias por producto (FOOD, Training). Una organización puede tener varias licencias activas.')}
          </p>
        </div>
        {canCreateHolding ? (
          <button type="button" className="btn-primary text-sm inline-flex items-center gap-2 shrink-0" onClick={() => setShowCreateHolding(true)}>
            <Plus className="w-4 h-4" />
            {t('holding.create_title', 'Nuevo holding (marca)')}
          </button>
        ) : null}
      </header>
      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-3">
        {tabs.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setOrgTab(id)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
              orgTab === id
                ? 'bg-stone-900 text-white'
                : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {orgTab === 'todas' && (
        <AdminCompaniesRegistry
          onExtendOperator={setExtendTarget}
          onManageLicenses={setLicenseTarget}
          reloadNonce={reloadNonce}
        />
      )}
      {orgTab === 'gimnasios' && (
        isFitLiveUiEnabled() ? (
          <FitLiveTrainersRoster />
        ) : sharedLoading ? (
          <p className="text-stone-500">{t('common.loading', 'Cargando...')}</p>
        ) : (
          <OrganizationsD28dTree
            gyms={gyms}
            users={users}
            onReload={loadShared}
            onOpenGymPanel={onOpenGymPanel}
            onExtendOperator={setExtendTarget}
            onManageLicenses={setLicenseTarget}
            onExtendEndUser={extendEndUser}
            extendingEndUser={extendSaving}
          />
        )
      )}
      {orgTab === 'coaches' && (
        <OrganizationsCoachList
          trainers={trainers}
          users={users}
          loading={sharedLoading}
          onSaveInviteCode={saveTrainerInviteCode}
          onManageLicenses={setLicenseTarget}
          onReload={loadShared}
          onOpenCreate={() => setShowCreateCoach(true)}
        />
      )}
      <ManageOrgLicensesModal
        open={Boolean(licenseTarget)}
        organizationName={licenseTarget?.orgName || ''}
        operator={licenseTarget?.operator || null}
        onClose={() => !extendSaving && setLicenseTarget(null)}
        onExtendOperator={setExtendTarget}
        onSaved={() => {
          setReloadNonce((n) => n + 1);
          loadShared();
        }}
      />

      <ExtendVigenciaModal
        open={Boolean(extendTarget)}
        userName={extendTarget?.userName || ''}
        planName={extendTarget?.planName || ''}
        moduleLabel={extendTarget?.moduleCode === 'training' ? 'Training' : 'D28D'}
        saving={extendSaving}
        onClose={() => !extendSaving && setExtendTarget(null)}
        onConfirm={confirmExtend}
      />
      <CreateHoldingModal
        open={showCreateHolding}
        onClose={() => setShowCreateHolding(false)}
        onCreated={() => {
          setReloadNonce((n) => n + 1);
          loadShared();
        }}
      />
      <CreateCoachOrgModal
        open={showCreateCoach}
        onClose={() => setShowCreateCoach(false)}
        onCreated={() => {
          setReloadNonce((n) => n + 1);
          loadShared();
        }}
      />
    </div>
  );
}
